import { ApiError } from "@groundspark/browser-auth";
import { button, buttonIcon, element, heading } from "@groundspark/browser-ui";
import { breadcrumbs } from "./breadcrumbs";
import type { BranchApi } from "./branch-api";
import { confirmDeletion } from "./confirm-dialog";
import { Autosave, type SaveState } from "./autosave";
import {
  channelLabels,
  type Channel,
  type Communication,
  type CommunicationDraft,
  type DraftContent,
  type Member,
} from "./domain";
import { emailDisplayText } from "./email-content";
import { EmailEditor } from "./email-editor";
import { memberTable } from "./member-table";

export class CommunicationComposer {
  public readonly element = element("section", "composer");
  private editor: EmailEditor | null = null;
  private request: AbortController | null = null;
  private title = "";
  private previewText = "";
  private body = "";
  private recipients: readonly Member[] | null = null;
  private selected = new Set<string>();
  private readonly channel: Channel;
  private readonly autosave: Autosave<DraftContent, CommunicationDraft>;
  private readonly id: string;
  private busy = false;
  private submissionRevision: number | null = null;
  private readonly saveStatus = element("p", "muted", "Draft saved");
  private readonly saveError = element("p", "composer__error");
  private readonly retry = button("Retry saving", "button button--white", () => {
    void this.save();
  });
  private readonly reload = button("Reload saved draft", "button button--white", () => {
    if (window.confirm("Reload the saved draft? Your unsaved changes will be replaced."))
      void this.onReload();
  });
  private readonly remove = button("Delete draft", "button button--danger", () =>
    this.deleteDraft(),
  );
  private readonly beforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.autosave.dirty || this.busy || this.submissionRevision !== null) {
      void this.save();
      event.preventDefault();
      event.returnValue = "";
    }
  };
  private readonly visibilityChange = (): void => {
    if (document.visibilityState === "hidden") void this.save();
  };
  private readonly online = (): void => {
    void this.save();
  };

  public constructor(
    draft: CommunicationDraft,
    private readonly branchId: string,
    private readonly api: BranchApi,
    private readonly onCommunications: () => void | Promise<void>,
    onSaved: (draft: CommunicationDraft) => void,
    private readonly onReload: () => Promise<void>,
    private readonly onFailure: (error: unknown) => boolean,
    private readonly onSubmitted: (communication: Communication) => void,
    private readonly onDeleted: () => Promise<void>,
  ) {
    this.id = draft.id;
    this.channel = draft.channel;
    this.title = draft.title ?? "";
    this.previewText = draft.previewText ?? "";
    this.body = draft.body;
    this.selected = new Set(draft.recipientIds);
    this.saveStatus.setAttribute("role", "status");
    this.saveError.setAttribute("role", "alert");
    this.retry.prepend(buttonIcon("save"));
    this.remove.prepend(buttonIcon("delete"));
    this.retry.hidden = true;
    this.reload.hidden = true;
    this.autosave = new Autosave(
      draft.revision,
      () => this.content(),
      (revision, content) => this.api.saveDraft(this.branchId, draft.id, revision, content),
      onSaved,
      (state, error) => this.showSaveState(state, error),
    );
    window.addEventListener("beforeunload", this.beforeUnload);
    window.addEventListener("online", this.online);
    document.addEventListener("visibilitychange", this.visibilityChange);
    this.renderEditor();
  }

  public save(): Promise<boolean> {
    if (this.busy || this.submissionRevision !== null) return Promise.resolve(false);
    return this.autosave.flush();
  }

  private async submit(): Promise<void> {
    if (this.busy || this.channel === "text" || this.selected.size === 0) return;
    this.busy = true;
    this.element.inert = true;
    try {
      if (this.submissionRevision === null) {
        if (!(await this.autosave.flush())) return;
        this.submissionRevision = this.autosave.savedRevision;
      }
      this.saveStatus.textContent = "Sending…";
      const sent = await this.api.submit(this.branchId, this.id, this.submissionRevision);
      this.busy = false;
      this.onSubmitted(sent);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status !== null &&
        error.status >= 400 &&
        error.status < 500
      )
        this.submissionRevision = null;
      if (!this.onFailure(error)) {
        this.saveStatus.textContent = "Send not confirmed";
        this.saveError.textContent = `${error instanceof Error ? error.message : String(error)} You can retry sending; the message will not be submitted twice.`;
      }
    } finally {
      this.busy = false;
      this.element.inert = false;
      this.remove.disabled = this.submissionRevision !== null;
      for (const control of this.element.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
        "input, .breadcrumbs__link",
      ))
        control.disabled = this.submissionRevision !== null;
    }
  }

  private async deleteDraft(): Promise<void> {
    if (this.busy || this.submissionRevision !== null || this.request !== null) return;
    const title =
      this.title.trim() ||
      this.displayBody().slice(0, 100).trim() ||
      `Untitled ${channelLabels[this.channel].toLowerCase()}`;
    const request = new AbortController();
    this.request = request;
    this.busy = true;
    try {
      const confirmed = await confirmDeletion(
        `delete-draft-${this.id}`,
        "Delete draft?",
        `“${title}” will be permanently deleted. This cannot be undone.`,
        "Delete draft",
        request.signal,
      );
      if (!confirmed || this.request !== request) return;
      this.element.inert = true;
      if (!(await this.autosave.flush()) || this.request !== request) return;
      this.saveStatus.textContent = "Deleting draft…";
      await this.api.deleteDraft(
        this.branchId,
        this.id,
        this.autosave.savedRevision,
        request.signal,
      );
      if (this.request !== request || request.signal.aborted) return;
      await this.onDeleted();
    } catch (error) {
      if (this.request !== request || request.signal.aborted) return;
      if (!this.onFailure(error)) {
        this.saveStatus.textContent = "Draft not deleted";
        this.saveError.textContent = error instanceof Error ? error.message : String(error);
        this.reload.hidden = !(error instanceof ApiError && error.status === 409);
      }
    } finally {
      if (this.request === request) this.request = null;
      this.busy = false;
      this.element.inert = false;
    }
  }

  private content(): DraftContent {
    return {
      title: this.channel === "text" ? null : this.title,
      previewText: this.previewText.trim() ? this.previewText : null,
      body: this.body,
      recipientIds: [...this.selected].sort(),
    };
  }

  private changed(): void {
    this.autosave.change();
  }

  private showSaveState(state: SaveState, error?: unknown): void {
    const labels = {
      saved: "Draft saved",
      waiting: "Unsaved changes",
      saving: "Saving draft…",
      error: "Draft not saved",
    };
    this.saveStatus.textContent = labels[state];
    this.saveError.textContent =
      state === "error"
        ? `Your changes are still in this editor. ${error instanceof Error ? error.message : String(error)}`
        : "";
    const conflict = state === "error" && error instanceof ApiError && error.status === 409;
    this.retry.hidden = state !== "error" || conflict;
    this.reload.hidden = !conflict;
    // A draft may have been deleted in another editor; retain local changes.
    if (state === "error" && !(error instanceof ApiError && error.status === 404))
      this.onFailure(error);
  }

  public destroy(): void {
    this.autosave.destroy();
    window.removeEventListener("beforeunload", this.beforeUnload);
    window.removeEventListener("online", this.online);
    document.removeEventListener("visibilitychange", this.visibilityChange);
    this.request?.abort();
    this.request = null;
    this.editor?.destroy();
    this.editor = null;
    this.element.replaceChildren();
    this.recipients = null;
    this.selected.clear();
  }

  private breadcrumbs(step: "message" | "recipients"): HTMLElement {
    const draftLabel = `${channelLabels[this.channel]} draft`;
    return breadcrumbs(step === "message" ? draftLabel : "Recipients", [
      { label: "Communications", onSelect: this.onCommunications },
      ...(step === "recipients"
        ? [{ label: draftLabel, onSelect: () => this.renderEditor() }]
        : []),
    ]);
  }

  private replaceContent(
    step: "message" | "recipients",
    ...content: readonly HTMLElement[]
  ): void {
    this.remove.disabled = this.request !== null || this.submissionRevision !== null;
    this.element.replaceChildren(
      element(
        "header",
        "composer__navigation",
        this.breadcrumbs(step),
        element(
          "div",
          "composer__controls",
          this.saveStatus,
          this.retry,
          this.reload,
          this.remove,
        ),
      ),
      this.saveError,
      ...content,
    );
    const title = this.element.querySelector("h2");
    if (title !== null && this.element.isConnected) {
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
      this.element.scrollIntoView({ block: "start" });
    }
  }

  private renderEditor(error?: string): void {
    this.request?.abort();
    this.request = null;
    this.editor?.destroy();
    this.editor = null;
    const form = element("form", "composer__form");
    const messageError = element("p", "composer__error", error ?? "");
    messageError.setAttribute("role", "alert");
    if (this.channel !== "text") {
      const title = element("input", "field__control");
      title.name = this.channel === "email" ? "subject" : "title";
      title.required = true;
      title.maxLength = 200;
      title.value = this.title;
      title.addEventListener("input", () => {
        this.title = title.value;
        this.changed();
      });
      form.append(
        element(
          "label",
          "field",
          element("span", "field__label", this.channel === "email" ? "Subject" : "Title"),
          title,
        ),
      );
    }
    if (this.channel === "email") {
      const preview = element("input", "field__control");
      preview.name = "preview_text";
      preview.maxLength = 200;
      preview.value = this.previewText;
      preview.addEventListener("input", () => {
        this.previewText = preview.value;
        this.changed();
      });
      this.editor = new EmailEditor(this.body, (html) => {
        this.body = html;
        this.changed();
      });
      form.append(
        element(
          "label",
          "field",
          element("span", "field__label", "Preview text (optional)"),
          preview,
          element("span", "muted", "A short preview shown beside the subject in an inbox."),
        ),
        element(
          "div",
          "field",
          element("span", "field__label", "Email message"),
          this.editor.element,
        ),
      );
    } else {
      const message = element("textarea", "field__control composer__message");
      message.name = "message";
      message.rows = 8;
      message.required = true;
      message.maxLength = 10_000;
      message.value = this.body;
      message.addEventListener("input", () => {
        this.body = message.value;
        this.changed();
      });
      form.append(
        element("label", "field", element("span", "field__label", "Message"), message),
      );
    }
    const next = element("button", "button button--dark-blue", "Continue");
    next.type = "submit";
    form.append(messageError, element("div", "composer__actions", next));
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      if (!this.displayBody().trim() || (this.channel !== "text" && !this.title.trim())) {
        messageError.textContent =
          "Enter a message and, for email or app notifications, a subject or title before continuing.";
        return;
      }
      if (this.recipients !== null) this.renderRecipients();
      else void this.loadRecipients();
    });
    this.replaceContent(
      "message",
      heading(2, `${channelLabels[this.channel]} draft`, "panel-heading__title"),
      form,
    );
  }

  private displayBody(): string {
    return this.channel === "email" ? emailDisplayText(this.body) : this.body;
  }

  private async loadRecipients(): Promise<void> {
    this.editor?.destroy();
    this.editor = null;
    const request = new AbortController();
    this.request = request;
    const progress = element("p", null, "Loading all branch members…");
    progress.setAttribute("role", "status");
    this.replaceContent(
      "recipients",
      heading(2, "Choose recipients", "panel-heading__title"),
      progress,
    );
    try {
      const members = new Map<string, Member>();
      const cursors = new Set<string>();
      let cursor: string | null = null;
      do {
        const page = await this.api.listMembers(this.branchId, cursor, request.signal);
        if (this.request !== request) return;
        for (const member of page.items) members.set(member.id, member);
        cursor = page.nextCursor;
        if (cursor !== null && cursors.has(cursor))
          throw new ApiError("The member list did not advance. Please try again.");
        if (cursor !== null) cursors.add(cursor);
      } while (cursor !== null);
      this.recipients = [...members.values()];
      const selected = new Set([...this.selected].filter((id) => members.has(id)));
      if (selected.size !== this.selected.size) {
        this.selected = selected;
        this.changed();
      }
      this.request = null;
      this.renderRecipients();
    } catch (error) {
      if (this.request !== request || request.signal.aborted) return;
      this.request = null;
      if (!this.onFailure(error))
        this.renderEditor(error instanceof Error ? error.message : String(error));
    }
  }

  private renderRecipients(): void {
    this.editor?.destroy();
    this.editor = null;
    const recipients = this.recipients ?? [];
    const send = button(
      `Send ${channelLabels[this.channel].toLowerCase()}`,
      "button button--dark-blue",
      () => this.submit(),
      this.channel === "text" || this.selected.size === 0,
    );
    const count = element("p", "muted");
    count.setAttribute("role", "status");
    const all = element("input", "check-row__input");
    all.type = "checkbox";
    all.disabled = recipients.length === 0;
    const checkboxes: HTMLInputElement[] = [];
    const list = memberTable(recipients, (member) => {
      const check = element("input", "check-row__input");
      check.type = "checkbox";
      check.checked = this.selected.has(member.id);
      check.value = member.id;
      check.setAttribute("aria-label", `Select ${member.displayName}`);
      check.addEventListener("change", () => {
        if (check.checked) this.selected.add(member.id);
        else this.selected.delete(member.id);
        updateSelection();
        this.changed();
      });
      checkboxes.push(check);
      return check;
    });
    const updateSelection = (): void => {
      send.disabled = this.channel === "text" || this.selected.size === 0;
      count.textContent = `${this.selected.size} of ${recipients.length} members selected`;
      all.checked = recipients.length > 0 && this.selected.size === recipients.length;
      all.indeterminate = this.selected.size > 0 && this.selected.size < recipients.length;
    };
    all.addEventListener("change", () => {
      this.selected = new Set(all.checked ? recipients.map((member) => member.id) : []);
      for (const checkbox of checkboxes) checkbox.checked = all.checked;
      updateSelection();
      this.changed();
    });
    updateSelection();
    const preview = element(
      "details",
      "message-preview",
      element("summary", null, "Review message"),
      this.channel !== "text" ? heading(3, this.title) : null,
      this.channel === "email" && this.previewText.trim()
        ? element("p", "muted", `Preview text: ${this.previewText}`)
        : null,
      element("p", "message-preview__body", this.displayBody()),
    );
    this.replaceContent(
      "recipients",
      heading(2, "Choose recipients", "panel-heading__title"),
      preview,
      count,
      recipients.length === 0
        ? element("p", null, "There are no members in this branch.")
        : element(
            "div",
            null,
            element("label", "check-row", all, element("strong", null, "Select all members")),
            list,
          ),
      element("div", "composer__actions", send),
    );
  }
}
